import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Download, FileText } from 'lucide-react';
import { subscriptionService, type Invoice } from '../../services/subscription.service';
import { Button } from '../shared/Button';

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency }).format(amount);

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    paid: 'bg-green-100 text-green-700',
    open: 'bg-yellow-100 text-yellow-700',
    void: 'bg-gray-100 text-gray-500',
    uncollectible: 'bg-red-100 text-red-700',
  };
  return map[status] ?? 'bg-gray-100 text-gray-500';
};

export const BillingHistory: React.FC = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    subscriptionService.getInvoices()
      .then(setInvoices)
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, []);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const { url } = await subscriptionService.getPortalLink();
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to get portal link:', error);
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 text-center text-gray-500">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin mx-auto mb-3" />
        <span>{t('payments.billingHistory.loading')}</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {invoices.length === 0 ? (
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-primary" />
          </div>
          <p className="text-gray-600 mb-6">{t('payments.billingHistory.noInvoices')}</p>
          <Button
            variant="secondary"
            onClick={handleOpenPortal}
            disabled={portalLoading}
            icon={<ExternalLink className="w-4 h-4" />}
          >
            {t('subscription.changePaymentMethod')}
          </Button>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-gray-600 font-semibold">
                  <th className="px-6 py-3">{t('payments.billingHistory.date')}</th>
                  <th className="px-6 py-3">{t('payments.billingHistory.description')}</th>
                  <th className="px-6 py-3">{t('payments.billingHistory.amount')}</th>
                  <th className="px-6 py-3">{t('payments.billingHistory.status')}</th>
                  <th className="px-6 py-3">{t('payments.billingHistory.invoice')}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, i) => (
                  <tr key={inv.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(inv.date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </td>
                    <td className="px-6 py-4">{inv.description}</td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      {formatCurrency(inv.amount, inv.currency)}
                      <span className="ml-1 text-xs text-gray-400">{t('subscription.inclVAT')}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadge(inv.status)}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {inv.pdfUrl ? (
                        <a
                          href={inv.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                        >
                          <Download className="w-4 h-4" />
                          {t('payments.billingHistory.download')}
                        </a>
                      ) : inv.hostedUrl ? (
                        <a
                          href={inv.hostedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {t('payments.billingHistory.invoice')}
                        </a>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {invoices.map((inv) => (
              <div key={inv.id} className="p-4">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-medium text-gray-900">{inv.description}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge(inv.status)}`}>{inv.status}</span>
                </div>
                <div className="flex justify-between items-center text-sm text-gray-600">
                  <span>{new Date(inv.date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(inv.amount, inv.currency)}</span>
                </div>
                {(inv.pdfUrl || inv.hostedUrl) && (
                  <a
                    href={(inv.pdfUrl || inv.hostedUrl)!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-primary mt-2 hover:underline"
                  >
                    <Download className="w-3 h-3" />
                    {t('payments.billingHistory.download')}
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Bottom: manage payment method */}
          <div className="border-t border-gray-100 p-4 flex justify-end">
            <Button
              variant="secondary"
              size="small"
              onClick={handleOpenPortal}
              disabled={portalLoading}
              icon={<ExternalLink className="w-4 h-4" />}
            >
              {t('subscription.changePaymentMethod')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default BillingHistory;
